import React, {
  CSSProperties,
  HTMLAttributes,
  memo,
  PropsWithChildren,
  useCallback,
} from 'react';
import { useIntersectionObserver, useResponsive } from 'app/hooks';
import { useDialog } from '../../dialog.context';
import { HStack, VStack, SVG } from '../../..';
import { Icons } from '../../../icons';
import { Overlay } from '../overlay/overlay';
import { Close } from '../close';
import {
  Arrow,
  ContentNavigation,
  PanelsWrapper,
  StyledAnchor,
} from '../../dialog.styled';
import { AnimatedScale, Center } from './content.styled';

type ContentProps = {
  withScrollArrows?: boolean;
  inert?: string;
  overlayStyle?: CSSProperties;
} & HTMLAttributes<HTMLDivElement>;

export const Content = memo(
  ({
    withScrollArrows = true,
    inert,
    overlayStyle,
    children,
    ...rest
  }: PropsWithChildren<ContentProps>) => {
    const { open, closing } = useDialog();
    const { viewport } = useResponsive();
    const [topAnchorRef, isTopVisible] = useIntersectionObserver({
      initialIsIntersecting: true,
    });
    const [bottomAnchorRef, isBottomVisible] = useIntersectionObserver({
      initialIsIntersecting: true,
    });

    const handleContentClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    const scrollTo = useCallback((direction: 'top' | 'bottom') => {
      const element = document.getElementById('dialog-overlay');

      element?.scrollTo({
        top: direction === 'top' ? 0 : element.scrollHeight,
        behavior: 'smooth',
      });
    }, []);

    if (!open && !closing) {
      return null;
    }

    return (
      <Overlay id="dialog-overlay" style={overlayStyle}>
        <Center>
          <HStack spacing="xs" style={{ position: 'relative' }}>
            <AnimatedScale $closing={closing} onClick={handleContentClick}>
              <StyledAnchor id="dialog-top-anchor" ref={topAnchorRef} />
              <PanelsWrapper {...rest}>{children}</PanelsWrapper>
              <StyledAnchor id="dialog-bottom-anchor" ref={bottomAnchorRef} />
            </AnimatedScale>

            {viewport.desktop && (
              <ContentNavigation>
                <Close asChild>
                  <div>
                    <SVG
                      src={Icons.Cross}
                      props={{
                        color: 'white',
                        cursor: 'pointer',
                        size: '24px',
                      }}
                    />
                  </div>
                </Close>

                {withScrollArrows && (
                  <VStack align="center">
                    <Arrow
                      $isShown={!isTopVisible}
                      onClick={() => scrollTo('top')}
                    >
                      <SVG
                        src={Icons.Arrow}
                        props={{
                          color: 'white',
                          cursor: 'pointer',
                          size: '24px',
                          deg: 90,
                        }}
                      />
                    </Arrow>

                    <Arrow
                      $isShown={!isBottomVisible}
                      onClick={() => scrollTo('bottom')}
                    >
                      <SVG
                        src={Icons.Arrow}
                        props={{
                          color: 'white',
                          cursor: 'pointer',
                          size: '24px',
                          deg: -90,
                        }}
                      />
                    </Arrow>
                  </VStack>
                )}
              </ContentNavigation>
            )}
          </HStack>
        </Center>
      </Overlay>
    );
  }
);

Content.displayName = 'Dialog.Content';
